/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ColDef, ColGroupDef, GridApi, GridOptions, GridReadyEvent} from 'ag-grid-community';
import {AgGridAngular} from 'ag-grid-angular';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {CommonHelperService} from '../../service/common-helper.service';
import {FieldRule} from '../../model/validation.model';
import {ValidationService} from '../../service/validation.service';
import {AttachmentCellRendererComponent} from './attachment-cell-renderer.component';
import {isRowWriteAllowedByMetadata} from '../../utils/row-write-control.util';
import {UrlCellRendererComponent} from './url-cell-renderer.component';

/**
 * One component the similar-components picker offers. `labels` holds the values of whichever columns the component
 * declared, keyed by member name, so this type does not have to know what any particular catalog shows.
 */
type DerivedIdOption = {
    id: number;
    labels: Record<string, string>;
    label: string;
    searchText: string;
};

@Component({
    selector: 'app-child-grid',
    standalone: true,
    imports: [CommonModule, FormsModule, AgGridAngular, AutoCompleteModule],
    template: `
        <ag-grid-angular
                        #agGrid
                        [style.display]="showChildGrid() ? '' : 'none'"
                        [attr.aria-hidden]="showChildGrid() ? null : 'true'"
                        [gridOptions]="gridOptions"
                        style="width: 100%; height: 400px;"
                        class="ag-theme-balham componentCatalog"
                        [undoRedoCellEditing]="true"
                        [enableCellTextSelection]="true"
                        [columnDefs]="childColumns"
                        [rowData]="childRowData"
                        [defaultColDef]="defaultColDef"
                        (gridReady)="onGridReady($event)"
                        [rowHeight]="60"
                        [pagination]="false">
        </ag-grid-angular>
        <div class="child-grid-footer" [style.display]="showFooter() ? 'flex' : 'none'">
            <ng-container *ngIf="!showDerivedAddControls; else derivedAddControls">
                <button type="button" class="p-button p-component p-button-sm gm-btn-primary" (click)="onAddRowClick()">
                    <span class="pi pi-plus"></span>
                    <span class="p-button-label">Add</span>
                </button>
            </ng-container>
            <ng-template #derivedAddControls>
                <p-autoComplete inputId="derived-id-select"
                                class="derived-add-autocomplete"
                                [(ngModel)]="selectedDerivedOption"
                                [suggestions]="filteredDerivedOptions"
                                (completeMethod)="onDerivedOptionSearch($event)"
                                [delay]="searchDebounceMs"
                                [forceSelection]="true"
                                [field]="'label'"
                                [ariaLabel]="derivedAddAriaLabel"
                                [placeholder]="derivedAddPlaceholder"
                                [appendTo]="'body'"></p-autoComplete>
                <button type="button"
                        class="p-button p-component p-button-sm gm-btn-primary"
                        [disabled]="!selectedDerivedOption"
                        (click)="onConfirmDerivedAdd()">
                    <span class="p-button-label">Confirm</span>
                </button>
                <button type="button"
                        class="p-button p-component p-button-sm p-button-text"
                        (click)="onCancelDerivedAdd()">
                    <span class="p-button-label">Cancel</span>
                </button>
            </ng-template>
        </div>
    `,
    styles: [`
        :host {
            display: block;
            width: 100%;
        }
        .child-grid-footer {
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: flex-end;
            margin-top: 8px;
        }
        .derived-add-autocomplete {
            min-width: 240px;
        }
        :host ::ng-deep .derived-add-autocomplete,
        :host ::ng-deep .derived-add-autocomplete .p-autocomplete,
        :host ::ng-deep .derived-add-autocomplete .p-autocomplete-input {
            width: 240px;
        }
        :host ::ng-deep .child-delete-action {
            color: #dc2626;
            border: none !important;
            box-shadow: none !important;
        }
        :host ::ng-deep .child-delete-action:hover {
            background: rgba(220, 38, 38, 0.12);
            color: #dc2626;
        }
        :host ::ng-deep .ag-theme-balham .ag-cell.error-row {
            background-color: rgba(220, 38, 38, 0.12) !important;
            border: 1px solid #dc2626 !important;
            padding-right: 1.5rem !important;
            padding-bottom: 1.05rem !important;
        }
        :host ::ng-deep .ag-theme-balham .ag-cell.error-row .ag-cell-value {
            color: #8b1e1e;
            font-weight: 600;
        }
        :host ::ng-deep .ag-theme-balham .ag-cell.error-row::before {
            content: "⚠";
            position: absolute;
            right: 0.35rem;
            top: 0.4rem;
            color: #b91c1c;
            font-size: 0.9rem;
            line-height: 1;
            pointer-events: none;
        }
        :host ::ng-deep .ag-theme-balham .ag-cell.error-row::after {
            content: "Invalid value!";
            position: absolute;
            right: 0.35rem;
            bottom: 0.2rem;
            color: #b91c1c;
            font-size: 0.72rem;
            line-height: 1;
            font-style: italic;
            pointer-events: none;
        }
    `]
})
export class ChildGridComponent implements AfterViewInit, OnChanges {
    @Input() colGroup!: ColGroupDef;
    @Input() rowData!: any;
    @Input() updatedRowData!: any;
    @Input() hasAdminPermission: boolean = false;
    @Input() fieldRulesByFieldName: Map<string, any> = new Map();
    @Output() dataChange = new EventEmitter<any>();

    @ViewChild('agGrid') agGrid!: AgGridAngular;

    childColumns: (ColDef | ColGroupDef)[] = [];
    childRowData: any[] = [];
    readonly loadingColumns = signal(true);
    gridApi!: GridApi;
    fieldPath: string = '';
    private readonly cellValidationMessages = new Map<string, string>();
    showDerivedAddControls = false;
    /** Shortest text the picker will send to the server. Mirrored in derivedAddPlaceholder, which tells the user. */
    static readonly MIN_SEARCH_CHARS = 3;
    readonly derivedAddPlaceholder = `Type ${ChildGridComponent.MIN_SEARCH_CHARS}+ characters to search`;
    /**
     * How long the picker waits after the last keystroke before searching the server.
     *
     * <p>A debounce only merges keystrokes closer together than its own delay. PrimeNG's default of 300ms is
     * shorter than the gap between keystrokes for anyone typing slower than roughly 40 words a minute, and what is
     * typed here is a part number read off another screen rather than prose - so at the default the debounce
     * coalesced almost nothing and a twelve-character part number could issue ten catalog searches, each casting
     * and ordering across every label column.
     *
     * <p>Kept under a second deliberately. This is dead time the user waits through after they have stopped
     * typing, on top of the round trip, and a typeahead that answers a second or more later stops reading as a
     * response to typing at all - people retype instead of waiting, which costs more searches than it saves.
     */
    static readonly SEARCH_DEBOUNCE_MS = 600;
    /** The template binds this rather than the static, which an Angular template cannot reach. */
    readonly searchDebounceMs = ChildGridComponent.SEARCH_DEBOUNCE_MS;
    /**
     * What the picker input answers to as a field, for anyone not reading the grid it sits under. It names no
     * catalog's fields, because each one labels its options with whatever it declares; and it is not the
     * placeholder, which is gone by the first keystroke.
     */
    readonly derivedAddAriaLabel = 'Component to add';
    /** Identifies the newest picker search, so a slower answer for older text cannot replace what is on screen. */
    private derivedSearchGeneration = 0;
    selectedDerivedOption: DerivedIdOption | null = null;
    derivedAvailableOptions: DerivedIdOption[] = [];
    filteredDerivedOptions: DerivedIdOption[] = [];

    defaultColDef: ColDef = {
        floatingFilter: true,
        resizable: true,
        sortable: true,
        filter: true,
        minWidth: 140,
        editable: params => this.hasAdminPermission &&
            isRowWriteAllowedByMetadata(params?.colDef, this.updatedRowData ?? this.rowData),
        cellStyle: {
            display: 'flex',
            alignItems: 'center'
        },
        wrapHeaderText: true,
        autoHeaderHeight: true,
        wrapText: true
    };

    gridOptions: GridOptions = {
        onCellValueChanged: (event) => {
            this.onCellValueChanged(event);
        }
    };

    constructor(private readonly validationService: ValidationService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['colGroup'] && this.colGroup) {
            this.setupColumns();
        }
        if (changes['rowData'] || changes['updatedRowData']) {
            this.loadChildData();
        }
    }

    ngAfterViewInit(): void {
        this.setupColumns();
        this.loadChildData();
    }

    private setupColumns(): void {
        this.loadingColumns.set(true);
        if (!this.colGroup || !this.colGroup.children) {
            this.loadingColumns.set(false);
            return;
        }

        // Use the full collection path when available (works for nested collections inside object tabs).
        this.fieldPath = this.resolveCollectionPath() || this.colGroup.groupId || '';

        // Filter out placeholder columns and extract child columns
        this.childColumns = this.colGroup.children
            .filter(col => {
                // Skip placeholder columns
                if ('colId' in col && col.colId?.endsWith('__placeholder')) {
                    return false;
                }
                return true; // Include both ColDefs and ColGroupDefs
            })
            .map(col => {
                // Handle ColDef (columns with fields)
                if ('field' in col && col.field) {
                    return this.mapChildColumn(col as ColDef);
                }
                // Handle ColGroupDef (nested column groups) - recursively process children
                if ('children' in col) {
                    const groupDef = col as ColGroupDef;
                    const processedChildren = groupDef.children
                        ?.filter(childCol => {
                            // Skip placeholder columns
                            if ('colId' in childCol && childCol.colId?.endsWith('__placeholder')) {
                                return false;
                            }
                            return true;
                        })
                        .map(childCol => {
                            // Process nested ColDefs
                            if ('field' in childCol && childCol.field) {
                                return this.mapChildColumn(childCol as ColDef);
                            }
                            return childCol;
                        }) || [];
                    return {
                        ...groupDef,
                        children: processedChildren
                    };
                }
                return col;
            }) as (ColDef | ColGroupDef)[];

        if (this.hasAdminPermission && this.isChildCollectionWritable()) {
            this.childColumns.unshift(this.createActionsColumn());
        }

        this.loadingColumns.set(false);
    }

    showChildGrid(): boolean {
        return !this.loadingColumns() && this.childColumns.length > 0;
    }

    showFooter(): boolean {
        return this.showChildGrid() && this.hasAdminPermission && this.isChildCollectionWritable();
    }

    private isChildCollectionWritable(): boolean {
        const sourceRow = this.updatedRowData ?? this.rowData;
        const hasWritableLeaf = (columns: (ColDef | ColGroupDef)[]): boolean => columns.some(col => {
            if ('children' in col && col.children) {
                return hasWritableLeaf(col.children);
            }
            const leaf = col as ColDef;
            if (!leaf.field || leaf.colId === '__childActions') {
                return false;
            }
            return isRowWriteAllowedByMetadata(leaf, sourceRow);
        });
        return hasWritableLeaf(this.childColumns);
    }

    private createActionsColumn(): ColDef {
        return {
            colId: '__childActions',
            headerName: '',
            width: 56,
            minWidth: 56,
            maxWidth: 56,
            editable: false,
            sortable: false,
            filter: false,
            suppressMovable: true,
            suppressNavigable: true,
            lockPosition: true,
            cellRenderer: (params: any) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.title = 'Delete row';
                button.className = 'action-button child-delete-action p-button p-component p-button-rounded p-button-text p-button-sm';
                button.innerHTML = '<span class="pi pi-trash"></span>';
                button.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.deleteChildRow(params?.node?.rowIndex);
                });
                return button;
            }
        };
    }

    private mapChildColumn(colDef: ColDef): ColDef {
        const fieldAfterArray = this.getFieldAfterLeadingCollection(colDef.field || '');
        const fieldChanged = !!colDef.field && fieldAfterArray !== colDef.field;
        const mappedCol = {
            ...colDef,
            field: fieldAfterArray || colDef.field
        } as ColDef;

        if ((mappedCol as any).esciDetailOnly === true) {
            mappedCol.hide = false;
        }

        if (fieldChanged && mappedCol.field) {
            mappedCol.valueGetter = (params) => CommonHelperService.getDeepValue(params?.data, mappedCol.field);
            mappedCol.valueSetter = (params) => {
                if (!params?.data) {
                    return false;
                }
                CommonHelperService.setDeepValue(params.data, mappedCol.field as string, params.newValue);
                return true;
            };
            if (mappedCol.cellEditorParams && typeof mappedCol.cellEditorParams === 'object') {
                const normalizedParams = {...mappedCol.cellEditorParams} as any;
                for (const key of ['fileIdField', 'nameField', 'pendingFileProperty']) {
                    if (typeof normalizedParams[key] === 'string' && normalizedParams[key].includes('[]')) {
                        normalizedParams[key] = this.getFieldAfterLeadingCollection(normalizedParams[key]);
                    }
                }
                mappedCol.cellEditorParams = normalizedParams;
            }
            if (mappedCol.cellRendererParams && typeof mappedCol.cellRendererParams === 'object') {
                const normalizedRendererParams = {...mappedCol.cellRendererParams} as any;
                for (const key of ['fileIdField', 'nameField', 'pendingFileProperty']) {
                    if (typeof normalizedRendererParams[key] === 'string' && normalizedRendererParams[key].includes('[]')) {
                        normalizedRendererParams[key] = this.getFieldAfterLeadingCollection(normalizedRendererParams[key]);
                    }
                }
                mappedCol.cellRendererParams = normalizedRendererParams;
            }
        }

        // Parent grid renders collection fields relative to the full row (e.g. cavityGroups[].field).
        // In the child grid each row is already an item from that collection, so the path must be relative.
        if (this.hasSubrowCellClass(mappedCol.cellClass) && mappedCol.field) {
            mappedCol.editable = undefined;
            mappedCol.suppressNavigable = undefined;
            mappedCol.autoHeight = undefined;

            if (mappedCol.field.includes('[]')) {
                const nestedPath = mappedCol.field;
                mappedCol.cellRenderer = (params) => {
                    const list = this.getNestedArray(params?.data, nestedPath);
                    return list.map(val => `<div class="subrow">${val ?? ''}</div>`).join('');
                };
            } else {
                const memberType = mappedCol.cellEditorParams?.memberType;
                if (memberType === 'attachment') {
                    const onAttachmentChanged = () => this.onChildAttachmentChanged();
                    mappedCol.cellEditorParams = {
                        ...(mappedCol.cellEditorParams as any || {}),
                        attachmentChanged: onAttachmentChanged
                    };
                    mappedCol.cellRendererParams = mappedCol.cellRendererParams || mappedCol.cellEditorParams;
                    mappedCol.cellRendererParams = {
                        ...(mappedCol.cellRendererParams as any || {}),
                        attachmentChanged: onAttachmentChanged
                    };
                    mappedCol.cellRenderer = AttachmentCellRendererComponent as any;
                } else if ((mappedCol.cellRendererParams as any)?.useValueAsUrl) {
                    mappedCol.cellRenderer = UrlCellRendererComponent as any;
                } else {
                    mappedCol.cellRenderer = undefined;
                }
            }
        }

        if (this.isReadOnlyDerivedGroup()) {
            // Derived row groups already contain flattened row objects, so parent-grid accessors/renderers
            // (which rely on the source row shape) produce empty values in details view.
            mappedCol.valueGetter = undefined;
            mappedCol.valueSetter = undefined;
            mappedCol.cellRenderer = undefined;
        }

        if (mappedCol.field) {
            const fieldRule = this.resolveFieldRule(mappedCol.field);
            if (fieldRule?.required) {
                const headerName = mappedCol.headerName || mappedCol.field;
                mappedCol.headerName = headerName.endsWith(' *') ? headerName : `${headerName} *`;
                mappedCol.headerTooltip = mappedCol.headerTooltip ?
                    `${mappedCol.headerTooltip} (Required)` : 'Required';
            }
            const existingCellClassRules = mappedCol.cellClassRules || {};
            mappedCol.cellClassRules = {
                ...existingCellClassRules,
                'error-row': (params: any) => !!this.getCellValidationMessage(params?.node?.rowIndex, mappedCol.field as string)
            };
            const existingTooltipValueGetter = mappedCol.tooltipValueGetter;
            mappedCol.tooltipValueGetter = (params: any) => {
                const validationMessage = this.getCellValidationMessage(params?.node?.rowIndex, mappedCol.field as string);
                if (validationMessage) {
                    return validationMessage;
                }
                if (typeof existingTooltipValueGetter === 'function') {
                    return existingTooltipValueGetter(params);
                }
                return null;
            };
        }

        return mappedCol;
    }

    private onChildAttachmentChanged(): void {
        // Attachment editors mutate row objects directly and may not emit a normal cell-value-changed event.
        // Force-sync child rows into parent row data so Apply/Save sees the latest collection attachment state.
        if (this.isReadOnlyDerivedGroup()) {
            return;
        }
        if (this.gridApi) {
            const allRowData: any[] = [];
            this.gridApi.forEachNode(node => {
                if (node.data) {
                    allRowData.push(structuredClone(node.data));
                }
            });
            this.applyChildRowsToParent(allRowData);
            return;
        }
        this.applyChildRowsToParent(this.childRowData || []);
    }

    private getFieldAfterLeadingCollection(field: string): string {
        const firstArrayIndex = field.indexOf('[]');
        if (firstArrayIndex < 0) {
            return field;
        }
        return field.substring(firstArrayIndex + 2).replace(/^\./, '') || field;
    }

    private resolveCollectionPath(): string {
        const fields: string[] = [];
        const collectFields = (cols: (ColDef | ColGroupDef)[]) => {
            for (const col of cols) {
                if ('children' in col && col.children) {
                    collectFields(col.children as (ColDef | ColGroupDef)[]);
                    continue;
                }
                if ('field' in col && col.field) {
                    fields.push(col.field);
                }
            }
        };
        collectFields(this.colGroup?.children || []);
        for (const field of fields) {
            const firstArrayIndex = field.indexOf('[]');
            if (firstArrayIndex > 0) {
                return field.substring(0, firstArrayIndex).replace(/\.$/, '');
            }
        }
        return '';
    }

    private hasSubrowCellClass(cellClass: ColDef['cellClass']): boolean {
        if (typeof cellClass === 'string') {
            return cellClass.includes('subrow-cell');
        }
        if (Array.isArray(cellClass)) {
            return cellClass.some(c => c.includes('subrow-cell'));
        }
        return false;
    }

    private getNestedArray(obj: any, path: string): any[] {
        if (!obj || !path) {
            return [];
        }

        const parts: string[] = path.split('.');
        const results: any[] = [];

        const recurse = (current: any, idx: number) => {
            if (idx >= parts.length) {
                results.push(current);
                return;
            }
            if (!current) {
                return;
            }
            let key = parts[idx];
            const isArray = key.endsWith('[]');
            if (isArray) {
                key = key.replace('[]', '');
                const arr = current[key];
                if (!Array.isArray(arr)) {
                    return;
                }
                for (const item of arr) {
                    recurse(item, idx + 1);
                }
            } else {
                recurse(current[key], idx + 1);
            }
        };

        recurse(obj, 0);
        return results;
    }

    private loadChildData(): void {
        const derivedResolver = (this.colGroup as any)?.esciDerivedRowsResolver;
        if (typeof derivedResolver === 'function') {
            const resolved = derivedResolver(this.updatedRowData ?? this.rowData);
            this.childRowData = Array.isArray(resolved) ? resolved.map((item: any) => structuredClone(item)) : [];
            this.refreshDerivedAvailableIds();
            this.cellValidationMessages.clear();
            if (this.gridApi) {
                this.gridApi.setGridOption('rowData', this.childRowData);
            }
            return;
        }
        if (!this.fieldPath || !this.updatedRowData) {
            this.childRowData = [];
            return;
        }

        // Get the child array from the rowData
        const childArray = CommonHelperService.getDeepValue(this.updatedRowData, this.fieldPath);
        
        if (Array.isArray(childArray)) {
            // Clone the array to avoid direct reference issues
            this.childRowData = childArray.map(item => structuredClone(item));
        } else {
            this.childRowData = [];
        }
        this.validateAllChildCells();

        // Refresh the grid if it's ready
        if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.childRowData);
        }
    }

    onGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this.loadChildData();
    }

    onCellValueChanged(event: any): void {
        if (!this.fieldPath || !this.updatedRowData || !this.gridApi) {
            return;
        }
        const rowIndex = event?.node?.rowIndex;
        const fieldName = event?.colDef?.field;
        const eventApi = event?.api;
        const eventNode = event?.node;
        const validationMessage = this.validateEditedCell(event);
        if (validationMessage) {
            if (typeof rowIndex === 'number' && fieldName) {
                this.setCellValidationMessage(rowIndex, fieldName, validationMessage);
            }
        } else {
            this.clearCellValidationMessage(rowIndex, fieldName);
        }

        // Get all row data from the grid
        const allRowData: any[] = [];
        this.gridApi.forEachNode(node => {
            if (node.data) {
                allRowData.push(structuredClone(node.data));
            }
        });

        this.applyChildRowsToParent(allRowData);

        // Refresh asynchronously so AG Grid keyboard handlers finish first (avoids key.length errors on Tab).
        if (eventApi?.refreshCells && eventNode && fieldName) {
            setTimeout(() => {
                eventApi.refreshCells({
                    rowNodes: [eventNode],
                    columns: [fieldName],
                    force: true
                });
            }, 0);
        }
    }

    onAddRowClick(): void {
        if (this.isMultiSelectIdsDerivedGroup()) {
            this.refreshDerivedAvailableIds();
            this.selectedDerivedOption = null;
            this.showDerivedAddControls = true;
            return;
        }
        if (!this.hasAdminPermission || !this.fieldPath || !this.updatedRowData) {
            return;
        }
        const newRow = this.createEmptyChildRow();
        this.childRowData = [...this.childRowData, newRow];
        if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.childRowData);
        }
        this.applyChildRowsToParent(this.childRowData);
        this.validateAllChildCells();
    }

    private createEmptyChildRow(): any {
        const row: any = {};
        const setDefaults = (cols: (ColDef | ColGroupDef)[]) => {
            for (const col of cols) {
                if ('children' in col && col.children) {
                    setDefaults(col.children as (ColDef | ColGroupDef)[]);
                    continue;
                }
                const field = (col as ColDef).field;
                if (!field || (col as ColDef).colId === '__childActions') {
                    continue;
                }
                CommonHelperService.setDeepValue(row, field, null);
            }
        };
        setDefaults(this.childColumns);
        return row;
    }

    private deleteChildRow(rowIndex: number): void {
        if (rowIndex === null || rowIndex === undefined || rowIndex < 0 || rowIndex >= this.childRowData.length) {
            return;
        }
        if (this.isMultiSelectIdsDerivedGroup()) {
            const row = this.childRowData[rowIndex] ?? {};
            const deletedSelectedId = Number((row as any).__selectedId ?? (row as any).id);
            if (!Number.isFinite(deletedSelectedId) || deletedSelectedId < 1) {
                return;
            }
            const currentIds = this.readCurrentDerivedIds();
            const updatedIds = currentIds.filter((id: number) => id !== deletedSelectedId);
            this.updateDerivedIds(updatedIds);
            return;
        }
        this.childRowData = this.childRowData.filter((_, idx) => idx !== rowIndex);
        if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.childRowData);
        }
        this.applyChildRowsToParent(this.childRowData);
        this.validateAllChildCells();
    }

    private applyChildRowsToParent(rows: any[]): void {
        if (this.isReadOnlyDerivedGroup()) {
            return;
        }
        const allRowData = rows.map(row => structuredClone(row));
        // Update the parent rowData with the modified child array
        CommonHelperService.setDeepValue(this.updatedRowData, this.fieldPath, allRowData);

        // Also update the original rowData to keep them in sync
        if (this.rowData) {
            CommonHelperService.setDeepValue(this.rowData, this.fieldPath, allRowData);
            this.updateParentCollectionChangedState(allRowData);
        }

        // Emit the data change event
        this.dataChange.emit(this.updatedRowData);
    }

    private updateParentCollectionChangedState(currentRows: any[]): void {
        if (!this.rowData?.originalRowData ||
            !this.fieldPath ||
            this.rowData.state === 'new' ||
            this.rowData.state === 'deleted') {
            return;
        }

        const originalRows = CommonHelperService.getDeepValue(this.rowData.originalRowData, this.fieldPath) ?? [];
        const valuesDiffer = JSON.stringify(originalRows) !== JSON.stringify(currentRows);
        if (!(this.rowData.changes instanceof Map)) {
            this.rowData.changes = new Map<string, string>();
        }

        if (valuesDiffer) {
            if (!this.rowData.changes.has(this.fieldPath)) {
                this.rowData.changes.set(this.fieldPath, `Original Value: ${JSON.stringify(originalRows)}`);
            }
            this.rowData.state = 'changed';
            return;
        }

        this.rowData.changes.delete(this.fieldPath);
        if (this.rowData.state === 'changed' && this.rowData.changes.size === 0) {
            this.rowData.state = 'saved';
        }
    }

    onConfirmDerivedAdd(): void {
        if (!this.isMultiSelectIdsDerivedGroup() || !this.selectedDerivedOption) {
            return;
        }
        const selectedNumericId = Number(this.selectedDerivedOption.id);
        if (!Number.isFinite(selectedNumericId) || selectedNumericId < 1) {
            return;
        }
        const currentIds = this.readCurrentDerivedIds();
        if (currentIds.includes(selectedNumericId)) {
            return;
        }
        this.updateDerivedIds([...currentIds, selectedNumericId]);
        this.selectedDerivedOption = null;
        this.showDerivedAddControls = false;
    }

    onCancelDerivedAdd(): void {
        this.showDerivedAddControls = false;
        this.selectedDerivedOption = null;
    }

    /**
     * Offers the components matching what has been typed so far. The catalog is searched on the server, because the
     * browser holds one page of it - filtering a local copy would mean being handed the whole table first, which is
     * exactly the cost server paging exists to avoid. Where no search resolver is wired (a client-mode grid, which
     * really does hold every row) the local list is filtered as before.
     *
     * A server search waits for MIN_SEARCH_CHARS. One or two characters match a large fraction of a catalog, so the
     * server spends the work to build a page of matches that is too broad to pick from anyway, and does it again on
     * the next keystroke. Filtering a local list is cheap, so a client-mode grid has no such floor.
     */
    onDerivedOptionSearch(event: any): void {
        const query = String(event?.query ?? '').trim();
        const searchIdOptions = (this.colGroup as any)?.esciSearchIdOptions;
        if (typeof searchIdOptions !== 'function') {
            this.filteredDerivedOptions = this.filterLocalDerivedOptions(query.toLowerCase());
            return;
        }
        if (query.length < ChildGridComponent.MIN_SEARCH_CHARS) {
            // Abandons any search still in flight: its answer is for text the user has since cut back below the
            // floor, and showing it would contradict the placeholder telling them to type more.
            this.derivedSearchGeneration++;
            this.filteredDerivedOptions = [];
            return;
        }
        const searchGeneration = ++this.derivedSearchGeneration;
        searchIdOptions(query).subscribe({
            next: (options: any[]) => {
                // A slower answer for text the user has already typed past must not replace what they see now.
                if (searchGeneration !== this.derivedSearchGeneration) {
                    return;
                }
                this.filteredDerivedOptions = this.withoutIdsAlreadyChosen(
                    this.normalizeDerivedIdOptions(Array.isArray(options) ? options : []));
            },
            error: (err: any) => {
                if (searchGeneration !== this.derivedSearchGeneration) {
                    return;
                }
                this.filteredDerivedOptions = [];
                console.error('Could not search for components to add', err);
            }
        });
    }

    private filterLocalDerivedOptions(query: string): DerivedIdOption[] {
        if (!query) {
            return [...this.derivedAvailableOptions];
        }
        return this.derivedAvailableOptions.filter((option: DerivedIdOption) => option.searchText.includes(query));
    }

    /** The member names shown beside an id, in the order the component declared them. */
    private getDerivedIdOptionLabelFields(): string[] {
        const fields = (this.colGroup as any)?.esciIdOptionLabelFields;
        return Array.isArray(fields) ? fields.filter((field: any) => typeof field === 'string' && field.length > 0)
                                     : [];
    }

    private isReadOnlyDerivedGroup(): boolean {
        return Boolean((this.colGroup as any)?.esciDerivedRowsReadOnly);
    }

    private isMultiSelectIdsDerivedGroup(): boolean {
        return (this.colGroup as any)?.esciDerivedRowsMode === 'multiSelectIds';
    }

    private getDerivedIdsFieldName(): string | null {
        const idsFieldName = (this.colGroup as any)?.esciIdsFieldName;
        return (typeof idsFieldName === 'string' && idsFieldName.trim().length > 0) ? idsFieldName : null;
    }

    private refreshDerivedAvailableIds(): void {
        if (!this.isMultiSelectIdsDerivedGroup()) {
            this.derivedAvailableOptions = [];
            this.filteredDerivedOptions = [];
            return;
        }
        const availableIdsResolver = (this.colGroup as any)?.esciAvailableIdsResolver;
        const options = (typeof availableIdsResolver === 'function') ? availableIdsResolver() : [];
        const normalizedOptions = this.normalizeDerivedIdOptions(Array.isArray(options) ? options : []);
        this.derivedAvailableOptions = this.withoutIdsAlreadyChosen(normalizedOptions);
        this.filteredDerivedOptions = [...this.derivedAvailableOptions];
    }

    /** Drops the components the row already points at: adding one twice is not a choice the picker should offer. */
    private withoutIdsAlreadyChosen(options: DerivedIdOption[]): DerivedIdOption[] {
        const existingIds = new Set(this.readCurrentDerivedIds().map((id: number) => String(id)));
        return options.filter((option: DerivedIdOption) => !existingIds.has(String(option.id)));
    }

    /**
     * Turns what the server (or the local list) offers into what the picker shows. The text beside the id is built
     * from whichever label fields the component declared, in that order, skipping any it has no value for - so a
     * catalog showing a part name and a plating reads "7 - Blade - Tin" and one showing nothing reads "7".
     *
     * <p>The given order is kept. A search is ordered on its label columns before the server applies its row limit,
     * so the sequence the answer arrives in is the set the server deliberately cut to; re-sorting by id here would
     * show a broad search in an order unrelated to it. The local resolver hands over its options in id order
     * already, so nothing is left needing a sort.
     */
    private normalizeDerivedIdOptions(options: any[]): DerivedIdOption[] {
        const labelFields = this.getDerivedIdOptionLabelFields();
        const normalized: DerivedIdOption[] = [];
        const seen = new Set<number>();
        for (const option of options) {
            const id = Math.trunc(Number((option as any)?.id ?? option));
            if (!Number.isFinite(id) || id < 1 || seen.has(id)) {
                continue;
            }
            seen.add(id);
            const labels = ((option as any)?.labels ?? {}) as Record<string, string>;
            const shown = labelFields.map(field => String(labels[field] ?? '').trim())
                                     .filter(value => value.length > 0);
            const label = [String(id), ...shown].join(' - ');
            const searchText = [String(id), ...shown].join(' ').toLowerCase();
            normalized.push({ id, labels, label, searchText });
        }
        return normalized;
    }

    private readCurrentDerivedIds(): number[] {
        const idsFieldName = this.getDerivedIdsFieldName();
        if (!idsFieldName) {
            return [];
        }
        const rawIds = CommonHelperService.getDeepValue(this.updatedRowData ?? this.rowData, idsFieldName);
        const rawList = Array.isArray(rawIds) ? rawIds : [];
        const parsedIds: number[] = [];
        for (const rawValue of rawList) {
            const numeric = Number(rawValue);
            if (!Number.isFinite(numeric)) {
                continue;
            }
            const id = Math.trunc(numeric);
            if (id < 1 || parsedIds.includes(id)) {
                continue;
            }
            parsedIds.push(id);
        }
        return parsedIds;
    }

    private updateDerivedIds(updatedIds: number[]): void {
        const idsFieldName = this.getDerivedIdsFieldName();
        if (!idsFieldName) {
            return;
        }
        CommonHelperService.setDeepValue(this.updatedRowData, idsFieldName, [...updatedIds]);
        if (this.rowData) {
            CommonHelperService.setDeepValue(this.rowData, idsFieldName, [...updatedIds]);
        }
        this.loadChildData();
        this.dataChange.emit(this.updatedRowData);
    }

    private validateEditedCell(event: any): string | null {
        const fieldName = event?.colDef?.field;
        if (!fieldName || !event?.data) {
            return null;
        }
        const fieldRule = this.resolveFieldRule(fieldName);
        if (!fieldRule) {
            return null;
        }
        const relativeFieldRule: FieldRule = {
            ...fieldRule,
            fieldName
        };
        return this.validationService.validateField(relativeFieldRule, event.data, false);
    }

    private resolveFieldRule(fieldName: string): FieldRule | undefined {
        return this.fieldRulesByFieldName.get(`${this.fieldPath}[].${fieldName}`) ||
               this.fieldRulesByFieldName.get(fieldName);
    }

    private validateAllChildCells(): void {
        this.cellValidationMessages.clear();
        if (this.isReadOnlyDerivedGroup()) {
            return;
        }
        const columns = ChildGridComponent.getLeafChildColumns(this.childColumns);
        this.childRowData.forEach((row, rowIndex) => {
            for (const column of columns) {
                const fieldName = column.field;
                if (!fieldName) {
                    continue;
                }
                const fieldRule = this.resolveFieldRule(fieldName);
                if (!fieldRule) {
                    continue;
                }
                const validationMessage = this.validationService.validateField(
                    {...fieldRule, fieldName}, row, false);
                if (validationMessage) {
                    this.setCellValidationMessage(rowIndex, fieldName, validationMessage);
                }
            }
        });
        if (typeof this.gridApi?.refreshCells === 'function') {
            this.gridApi.refreshCells({force: true});
        }
    }

    private static getLeafChildColumns(columns: (ColDef | ColGroupDef)[]): ColDef[] {
        return columns.flatMap(column =>
            'children' in column && column.children ?
                ChildGridComponent.getLeafChildColumns(column.children as (ColDef | ColGroupDef)[]) :
                [column as ColDef]);
    }

    private getCellValidationKey(rowIndex: number, fieldName: string): string {
        return `${rowIndex}:${fieldName}`;
    }

    private getCellValidationMessage(rowIndex: number, fieldName: string): string | null {
        if (typeof rowIndex !== 'number' || !fieldName) {
            return null;
        }
        return this.cellValidationMessages.get(this.getCellValidationKey(rowIndex, fieldName)) || null;
    }

    private setCellValidationMessage(rowIndex: number, fieldName: string, message: string): void {
        if (typeof rowIndex !== 'number' || !fieldName) {
            return;
        }
        this.cellValidationMessages.set(this.getCellValidationKey(rowIndex, fieldName), message);
    }

    private clearCellValidationMessage(rowIndex: number, fieldName: string): void {
        if (typeof rowIndex !== 'number' || !fieldName) {
            return;
        }
        this.cellValidationMessages.delete(this.getCellValidationKey(rowIndex, fieldName));
    }
}

